<template>
  <div class="credit-card-form">
    <div class="payment-card">
      <div class="payment-card__header">
        <div class="header-content">
          <div class="payment-card__title">{{ $t("auto.cardInfo") }}</div>
          <div class="iyzico-brand">
            <IyzicoPay class="iyzico-logo" />
          </div>
        </div>
      </div>
      <div class="payment-card__content">
        <div class="address-item full-width" :class="{ 'has-error': formErrors.cardHolder }">
          <a-icon type="user" />
          <a-input
            v-model="form.cardHolder"
            autocomplete="cc-name"
            name="cardholderName"
            id="cardholderName"
            @input="handleInput('cardHolder')"
            data-field="cardHolder"
            class="edit-input"
            :placeholder="$t('message.cardNameOnCardPlaceholder')"
          />
        </div>

        <div class="credit-card-columns">
          <div class="address-item" :class="{ 'has-error': formErrors.cardNumber }">
            <a-icon type="credit-card" />
            <a-input
              v-model="form.cardNumber"
              autocomplete="cc-number"
              name="cardNumber"
              id="cardNumber"
              inputmode="numeric"
              v-mask="'#### #### #### ####'"
              @input="handleInput('cardNumber')"
              data-field="cardNumber"
              class="edit-input"
              placeholder="**** **** **** ****"
              type="tel"
            />
          </div>

          <div class="address-item small-input" :class="{ 'has-error': formErrors.expiry }">
            <a-icon type="calendar" />
            <a-input
              v-model="form.expiry"
              autocomplete="cc-exp"
              name="ccExp"
              id="ccExp"
              inputmode="numeric"
              pattern="(0[1-9]|1[0-2])\/?([0-9]{2})"
              v-mask="'##/##'"
              @input="handleInput('expiry')"
              data-field="expiry"
              class="edit-input"
              placeholder="MM/YY*"
              type="tel"
            />
          </div>

          <div class="address-item small-input" :class="{ 'has-error': formErrors.cvc }">
            <a-icon type="safety-certificate" />
            <a-input
              v-model="form.cvc"
              autocomplete="cc-csc"
              name="cvc"
              id="cvc"
              inputmode="numeric"
              pattern="\d*"
              v-mask="'###'"
              @input="handleInput('cvc')"
              data-field="cvc"
              class="edit-input"
              placeholder="***"
              type="tel"
              maxlength="4"
            />
          </div>
        </div>
        
        <div class="payment-card__notice">
          <a-icon type="lock" theme="filled" />
          <span>{{ $t("auto.cardInfoSecurelyStored") }}</span>
        </div>
      </div>
    </div>
    
    <div class="modal-footer">
      <SupsisButton primary @click="submitForm">{{ $t("auto.save") }}</SupsisButton>
    </div>
  </div>
</template>

<script>
import SupsisButton from "../../../views/Pages/Settings/components/SupsisButton.vue";
import { mask } from 'vue-the-mask';
import IyzicoPay from "../../../components/svg/IyzicoPay.vue";

export default {
  name: "CreditCardForm",
  components: {
    SupsisButton,
    IyzicoPay
  },
  directives: {
    mask
  },
  props: {
    initialValues: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      form: {
        cardHolder: "",
        cardNumber: "",
        expiry: "",
        cvc: ""
      },
      formErrors: {}
    };
  },
  created() {
    if (this.initialValues) {
      this.form = { ...this.form, ...this.initialValues };
    }
  },
  methods: {
    handleInput(field) {
      if (this.formErrors[field]) {
        this.$delete(this.formErrors, field);
      }
    },
    validateForm() {
      this.formErrors = {};
      let isValid = true;
      
      if (!this.form.cardHolder.trim()) {
        this.formErrors.cardHolder = true;
        isValid = false;
      }
      
      const cleanedCardNumber = this.form.cardNumber.replace(/\s/g, "");
      if (!/^\d{16}$/.test(cleanedCardNumber)) {
        this.formErrors.cardNumber = true;
        isValid = false;
      }
      
      const expiryRegex = /^(0[1-9]|1[0-2])\/([0-9]{2})$/;
      if (!expiryRegex.test(this.form.expiry)) {
        this.formErrors.expiry = true;
        isValid = false;
      }
      
      if (this.form.cvc.length < 3) {
        this.formErrors.cvc = true;
        isValid = false;
      }
      
      if (!isValid) {
        this.$message.error("Lütfen kart bilgilerini doğru formatta giriniz");
        return false;
      }
      
      return true;
    },
    submitForm() {
      if (this.validateForm()) {
        const [expireMonth, expireYear] = this.form.expiry.split("/");
        
        const cardData = {
          holderName: this.form.cardHolder,
          cardNumber: this.form.cardNumber.replace(/\s/g, ""),
          expireMonth,
          expireYear: "20" + expireYear,
          cvc: this.form.cvc
        };
        
        this.$emit('submit', cardData);
      }
    }
  }
};
</script>

<style scoped>
.payment-card {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 10px;
  border: 1px solid #e8e8e8;
  transition: box-shadow 0.3s;
}

.payment-card__header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #edf2f7;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.payment-card__title {
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
  letter-spacing: -0.2px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.payment-card__title::before {
  content: "";
  display: block;
  width: 3px;
  height: 20px;
  background: #2f54eb;
  border-radius: 2px;
}

.payment-card__content {
  padding: 16px 0;
}

.address-item {
  display: flex;
  align-items: center;
  padding: 12px;
  background: #f8fafc;
  border-radius: 6px;
  margin-bottom: 16px;
}

.full-width {
  width: 100%;
}

.credit-card-columns {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 16px;
  margin: 12px 0;
}

.edit-input {
  background: #f8fafc;
  border: none;
  box-shadow: none;
  padding: 0 8px;
  flex: 1;
}

.edit-input:focus {
  box-shadow: 0 0 0 2px rgba(47, 84, 235, 0.2);
}

.edit-input::placeholder {
  color: #a0aec0;
  font-style: italic;
}

.small-input {
  font-size: 14px;
}

.address-item .anticon {
  margin-right: 12px;
  color: #667eea;
  flex-shrink: 0;
}

.payment-card__notice {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #edf2f7;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #38a169;
  font-size: 14px;
}

.iyzico-brand {
  display: flex;
  align-items: center;
}

.iyzico-logo {
  width: 80px;
  height: 24px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #edf2f7;
}

.has-error {
  .edit-input,
  .ant-input {
    border: 1px solid #ff4d4f !important;
    background-color: #fff1f0 !important;
  }

  .anticon {
    color: #ff4d4f !important;
  }
}
</style> 